import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SyncClient } from 'twilio-sync';
import * as Video from 'twilio-video';
import { 
  Button, 
  Card, 
  Space, 
  Typography, 
  Tooltip, 
  Row, 
  Col,
  message as antdMessage 
} from 'antd';
import {
  BsCameraVideoFill,
  BsCameraVideoOff,
  BsMicFill,
  BsMicMuteFill,
} from 'react-icons/bs';
import { TbScreenShareOff, TbScreenShare } from 'react-icons/tb';
import { CloseOutlined } from '@ant-design/icons';
import { 
  attachLocalTracks, 
  attachRemoteTracks, 
  detachTracks,
  cleanupTracks 
} from '../../utils/videoHelpers';
import { videoStyles } from '../../utils/videoStyles';
import { getSyncToken, getVideoToken } from '../../services/videoService';
import PostVideoCall from './PostVideoCall';

const { Text, Title } = Typography;

const VideoRoomWidget = ({ 
  code, 
  onCallEnd, 
  config = {},
  onError 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [postCall, setPostCall] = useState(false);
  const [localAudio, setLocalAudio] = useState(null);
  const [localVideo, setLocalVideo] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenTrack, setScreenTrack] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [buttonText, setButtonText] = useState('Join Video Room');
  const [agentConnected, setAgentConnected] = useState(false);
  const [joinError, setJoinError] = useState(false);
  const [syncError, setSyncError] = useState('');
  // Always use "Test12" as room ID
  const currentRoomCode = "Test12";

  // Check if Twilio Video is supported
  const isVideoSupported = Video.isSupported;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeRoom) {
        activeRoom.disconnect();
      }
      cleanupTracks('remote-media-div');
      cleanupTracks('local-media-div');
    };
  }, [activeRoom]);

  async function processSyncDataUpdate(data) {
    if (!activeRoom && data.room) {
      await connectVideo();
    } else {
      setJoinError(true);
    }
  }

  async function connectSync(roomCode) {
    setSyncError('');
    try {
      const response = await getSyncToken(roomCode);
      
      // Validate token before creating SyncClient
      if (!response.token || typeof response.token !== 'string') {
        throw new Error('Invalid sync token received from backend. Token is missing or invalid format.');
      }
      
      const client = new SyncClient(response.token);
      const document = await client.document(roomCode);
      
      console.log('Code Validated. Sync document SID:', document.sid);
      processSyncDataUpdate(document.data);
      
      document.on('updated', (event) => {
        console.log('Received an "updated" Sync Document event: ', event);
        setAgentConnected(true);
        setButtonText('Join Video Room');
        processSyncDataUpdate(event.data);
      });
    } catch (error) {
      console.error('Unexpected error in sync:', error);
      let errorMessage = error.message || 'Failed to connect to sync service';
      
      // Check for agent not available error (403 Invalid code)
      if (errorMessage === 'AGENT_NOT_AVAILABLE' || 
          (errorMessage.includes('403') && errorMessage.includes('Invalid code')) ||
          (errorMessage.includes('Invalid code'))) {
        errorMessage = 'Twilio agent is not available';
      } else if (errorMessage.includes('404') || errorMessage.includes('endpoint not configured')) {
        errorMessage = 'Video backend endpoint not found. Please configure the video backend URL or implement the required endpoints.';
      } else if (errorMessage.includes('token')) {
        errorMessage = 'Invalid token received. Please check backend configuration.';
      }
      
      setSyncError(errorMessage);
      setIsLoading(false);
      onError?.(error);
    }
  }

  async function connectVideo() {
    try {
      const roomCodeToUse = currentRoomCode || code;
      const response = await getVideoToken(roomCodeToUse);
      
      // Validate token before connecting
      if (!response.token || typeof response.token !== 'string') {
        throw new Error('Invalid video token received from backend. Token is missing or invalid format.');
      }
      
      const room = await Video.connect(response.token);
      
      setIsLoading(false);
      setButtonText('Connected!');
      setActiveRoom(room);

      // Get local tracks
      Array.from(room.localParticipant.tracks.values()).forEach((track) => {
        if (track.kind === 'audio') {
          setLocalAudio(track.track);
          setAudioEnabled(track.track.isEnabled);
        } else {
          setLocalVideo(track.track);
          setVideoEnabled(track.track.isEnabled);
        }
      });

      setupEventListeners(room);
      
      // Retry attaching local tracks after a short delay in case they're not immediately available
      setTimeout(() => {
        if (room && room.localParticipant) {
          const tracks = Array.from(room.localParticipant.tracks.values());
          if (tracks.length > 0) {
            cleanupTracks('local-media-div');
            attachLocalTracks(tracks, 'local-media-div');
          }
        }
      }, 500);
    } catch (error) {
      console.error(`Unable to connect to Room: ${error.message}`);
      let errorMessage = error.message;
      
      // Check for agent not available error (403 Invalid code)
      if (errorMessage === 'AGENT_NOT_AVAILABLE' || 
          (errorMessage.includes('403') && errorMessage.includes('Invalid code')) ||
          (errorMessage.includes('Invalid code'))) {
        errorMessage = 'Twilio agent is not available';
        setSyncError(errorMessage);
      }
      
      antdMessage.error(`Unable to connect to Room: ${errorMessage}`);
      setIsLoading(false);
      onError?.(error);
    }
  }

  function setupEventListeners(room) {
    console.log('Successfully joined a Room:', room);
    const localParticipant = room.localParticipant;
    console.log(
      `Connected to the Room as LocalParticipant "${localParticipant.identity}"`
    );

    const remoteMedia = 'remote-media-div';
    const localMedia = 'local-media-div';

    // Function to attach local tracks to DOM
    const attachLocalTracksToDOM = () => {
      const tracks = Array.from(localParticipant.tracks.values());
      cleanupTracks(localMedia); // Clear existing tracks first
      attachLocalTracks(tracks, localMedia);
      console.log('Local tracks attached to DOM:', tracks.length);
    };

    // Attach local tracks immediately
    attachLocalTracksToDOM();

    // Handle when local tracks are enabled
    localParticipant.on('trackEnabled', (track) => {
      console.log('Local track enabled:', track);
      attachLocalTracksToDOM(); // Re-attach all tracks
    });

    // Handle when local tracks are disabled
    localParticipant.on('trackDisabled', (track) => {
      console.log('Local track disabled:', track);
      attachLocalTracksToDOM(); // Re-attach all tracks
    });

    // Handle when local tracks are published
    localParticipant.on('trackPublished', (publication) => {
      console.log('Local track published:', publication);
      attachLocalTracksToDOM(); // Re-attach all tracks
    });

    // Handle when local tracks are unpublished
    localParticipant.on('trackUnpublished', (publication) => {
      console.log('Local track unpublished:', publication);
      attachLocalTracksToDOM(); // Re-attach all tracks
    });

    // Add existing participant tracks
    room.participants.forEach((participant) => {
      console.log(
        `IncomingVideoComponent: ${participant.identity} is already in the room`
      );
      const tracks = Array.from(participant.tracks.values());
      attachRemoteTracks(tracks, remoteMedia);
    });

    // When a Participant joins the Room
    room.on('participantConnected', (participant) => {
      console.log(
        `IncomingVideoComponent: ${participant.identity} joined the room`
      );
    });

    // When a participant adds a track, attach it
    room.on('trackSubscribed', (track, participant) => {
      console.log(
        `IncomingVideoComponent: ${participant} added track: ${track.kind}`
      );
      attachRemoteTracks([track], remoteMedia);
    });

    // When a Participant removes a Track, detach it from the DOM
    room.on('trackUnsubscribed', (track, participant) => {
      console.log(
        `IncomingVideoComponent: ${participant} removed track: ${track.kind}`
      );
      detachTracks([track]);
    });

    // When a Participant leaves the Room
    room.on('participantDisconnected', (participant) => {
      console.log(
        `IncomingVideoComponent: ${participant.identity} left the room`
      );
    });

    // Room disconnected
    room.on('disconnected', () => {
      console.log('IncomingVideoComponent: disconnected');
      setActiveRoom(null);
      cleanupTracks('remote-media-div');
      cleanupTracks('local-media-div');
    });
  }

  async function joinVideoClicked() {
    setIsLoading(true);
    await connectSync(currentRoomCode);
  }

  function mute() {
    console.log('mute clicked');
    if (localAudio) {
      localAudio.disable();
      setAudioEnabled(false);
    }
  }

  function unMute() {
    console.log('unmute clicked');
    if (localAudio) {
      localAudio.enable();
      setAudioEnabled(true);
    }
  }

  function videoOn() {
    if (localVideo) {
      localVideo.enable();
      setVideoEnabled(true);
      // Re-attach local tracks after enabling
      setTimeout(() => {
        if (activeRoom) {
          const tracks = Array.from(activeRoom.localParticipant.tracks.values());
          cleanupTracks('local-media-div');
          attachLocalTracks(tracks, 'local-media-div');
        }
      }, 100);
    }
  }

  function videoOff() {
    if (localVideo) {
      localVideo.disable();
      setVideoEnabled(false);
      // Re-attach local tracks after disabling (to show black screen)
      setTimeout(() => {
        if (activeRoom) {
          const tracks = Array.from(activeRoom.localParticipant.tracks.values());
          cleanupTracks('local-media-div');
          attachLocalTracks(tracks, 'local-media-div');
        }
      }, 100);
    }
  }

  function screenCapture() {
    if (!screenTrack) {
      navigator.mediaDevices
        .getDisplayMedia()
        .then((stream) => {
          const newScreenTrack = new Video.LocalVideoTrack(stream.getTracks()[0]);
          activeRoom?.localParticipant.publishTrack(newScreenTrack);
          setScreenTrack(newScreenTrack);
        })
        .catch((error) => {
          console.error('Screen share error:', error);
          antdMessage.error('Could not share the screen.');
        });
    } else {
      activeRoom?.localParticipant.unpublishTrack(screenTrack);
      screenTrack.stop();
      setScreenTrack(null);
    }
  }

  function disconnect() {
    if (activeRoom) {
      activeRoom.disconnect();
      setActiveRoom(null);
      cleanupTracks('remote-media-div');
      cleanupTracks('local-media-div');
      setPostCall(true);
    }
  }

  // Check if browser is supported
  if (!isVideoSupported) {
    return (
      <div style={videoStyles.centerContent}>
        <Card>
          <Title level={4}>Browser or context not supported</Title>
          <Text>
            Please open this application in one of the{' '}
            <a
              href="https://www.twilio.com/docs/video/javascript#supported-browsers"
              target="_blank"
              rel="noopener noreferrer"
            >
              supported browsers
            </a>
            . If you are using a supported browser, please ensure that this app
            is served over a{' '}
            <a
              href="https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts"
              target="_blank"
              rel="noopener noreferrer"
            >
              secure context
            </a>{' '}
            (e.g. https or localhost).
          </Text>
        </Card>
      </div>
    );
  }

  // Post-call view
  if (postCall) {
    return <PostVideoCall onReturnToChat={onCallEnd} config={config} />;
  }

  // Pre-join view
  if (!activeRoom) {
    return (
      <div style={videoStyles.centerContent}>
        <div style={videoStyles.maxWidthDiv}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Title level={4}>User View</Title>
            </div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {joinError && (
                <Text type={agentConnected ? 'default' : 'secondary'}>
                  {agentConnected
                    ? 'Agent connected.'
                    : 'Waiting for agent to join...'}
                </Text>
              )}
              {syncError && (
                <Text type="danger">Error: {syncError}</Text>
              )}
              <Button
                type="primary"
                danger
                onClick={joinVideoClicked}
                loading={isLoading}
                block
                style={{ background: '#F22F46', borderColor: '#F22F46' }}
              >
                {buttonText}
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    );
  }

  // Active video room view
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', margin: 0, padding: 0 }}>
      <div style={{ padding: '10px', textAlign: 'center', flexShrink: 0, backgroundColor: '#1a1a1a' }}>
        <Title level={4} style={{ color: 'white', margin: 0 }}>Video Room</Title>
      </div>
      
      <Row gutter={0} style={{ flex: 1, margin: 0, padding: 0, minHeight: 0, overflow: 'hidden', width: '100%' }}>
        <Col xs={24} md={12} style={{ padding: 0, margin: 0, height: '100%' }}>
          <div style={{ ...videoStyles.participantContainer, margin: 0, padding: 0, height: '100%' }}>
            <div id="remote-media-div" style={{ ...videoStyles.videoDivContainer, margin: 0, padding: 0, borderRadius: 0, height: '100%' }}></div>
            <div style={videoStyles.overlayContent}>
              <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>Twilio Agent</Text>
            </div>
          </div>
        </Col>
        <Col xs={24} md={12} style={{ padding: 0, margin: 0, height: '100%' }}>
          <div style={{ ...videoStyles.participantContainer, margin: 0, padding: 0, height: '100%' }}>
            <div id="local-media-div" style={{ ...videoStyles.videoDivContainer, margin: 0, padding: 0, borderRadius: 0, height: '100%' }}></div>
            <div style={videoStyles.overlayContent}>
              <Text strong style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>Calling Citizen</Text>
            </div>
          </div>
        </Col>
      </Row>

      <div style={{
        ...videoStyles.footerDiv,
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '0',
        margin: 0,
        marginTop: 'auto',
        flexShrink: 0,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
        position: 'relative',
        width: '100%'
      }}>
        <Space size="large">
          <Tooltip title={audioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}>
            {audioEnabled ? (
              <Button
                type="default"
                size="large"
                shape="circle"
                icon={<BsMicFill style={{ width: '24px', height: '24px' }} />}
                onClick={mute}
                style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#2a2a2a',
                  borderColor: '#404040',
                  color: '#fff'
                }}
              />
            ) : (
              <Button
                type="primary"
                danger
                size="large"
                shape="circle"
                icon={<BsMicMuteFill style={{ width: '24px', height: '24px' }} />}
                onClick={unMute}
                style={{
                  width: '56px',
                  height: '56px'
                }}
              />
            )}
          </Tooltip>
          
          <Tooltip title={videoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}>
            {videoEnabled ? (
              <Button
                type="default"
                size="large"
                shape="circle"
                icon={<BsCameraVideoFill style={{ width: '24px', height: '24px' }} />}
                onClick={videoOff}
                style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#2a2a2a',
                  borderColor: '#404040',
                  color: '#fff'
                }}
              />
            ) : (
              <Button
                type="primary"
                danger
                size="large"
                shape="circle"
                icon={<BsCameraVideoOff style={{ width: '24px', height: '24px' }} />}
                onClick={videoOn}
                style={{
                  width: '56px',
                  height: '56px'
                }}
              />
            )}
          </Tooltip>
          
          <Tooltip title={screenTrack ? 'Stop Sharing Screen' : 'Share Screen'}>
            <Button
              type={screenTrack ? 'primary' : 'default'}
              size="large"
              shape="circle"
              icon={
                screenTrack ? (
                  <TbScreenShareOff style={{ width: '24px', height: '24px' }} />
                ) : (
                  <TbScreenShare style={{ width: '24px', height: '24px' }} />
                )
              }
              onClick={screenCapture}
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: screenTrack ? undefined : '#2a2a2a',
                borderColor: screenTrack ? undefined : '#404040',
                color: '#fff'
              }}
            />
          </Tooltip>
          
          <Tooltip title="End Call">
            <Button
              type="primary"
              danger
              size="large"
              shape="circle"
              icon={<CloseOutlined style={{ width: '24px', height: '24px' }} />}
              onClick={disconnect}
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#f5222d',
                borderColor: '#f5222d'
              }}
            />
          </Tooltip>
        </Space>
      </div>
    </div>
  );
};

VideoRoomWidget.propTypes = {
  code: PropTypes.string.isRequired,
  onCallEnd: PropTypes.func,
  config: PropTypes.object,
  onError: PropTypes.func,
};

export default VideoRoomWidget;

